import type {
  ExpenseBucket,
  ExpenseCategory,
  IncomeStatement,
  RevenueBucket,
} from '@/domains/financials/services/adminFinancialsService';
import { EXPENSE_CATEGORY_LABEL } from '@/domains/financials/services/adminFinancialsService';
import { Card } from '@sighthost/ui';

/**
 * F-08. These were two more local `Intl.NumberFormat` copies, and this file
 * RE-EXPORTED them to four sibling pages — so one drift here would have moved
 * every financial figure in the section at once.
 *
 * Re-exported rather than deleted because the four consumers import them from
 * here by name; pointing the name at the canonical helper fixes the source of
 * truth without a rename across the section. `formatZARShort` maps to
 * `formatZARWhole`, which is the same intent (no decimals) under the name the
 * design system already uses for it.
 */
export { formatZAR, formatZARWhole as formatZARShort } from '@/shared/utils/format';
import { formatZAR } from '@/shared/utils/format';

export function deltaPct(a: number, b: number): string {
  if (b === 0) return a === 0 ? '0%' : '∞';
  const d = ((a - b) / Math.abs(b)) * 100;
  const sign = d >= 0 ? '+' : '';
  return `${sign}${d.toFixed(1)}%`;
}

const REV_ROWS: Array<{ key: keyof RevenueBucket; label: string }> = [
  { key: 'hosting', label: 'Hosting (renewals + new + plan changes)' },
  { key: 'domains', label: 'Domains (registrations + renewals)' },
  { key: 'other', label: 'Other / one-off' },
];

const EXP_ROWS: Array<{ key: ExpenseCategory; label: string }> = [
  { key: 'whm_hosting', label: EXPENSE_CATEGORY_LABEL.whm_hosting },
  { key: 'domains_topup', label: EXPENSE_CATEGORY_LABEL.domains_topup },
  { key: 'payfast_fees', label: EXPENSE_CATEGORY_LABEL.payfast_fees },
  { key: 'internet', label: EXPENSE_CATEGORY_LABEL.internet },
  { key: 'infrastructure', label: EXPENSE_CATEGORY_LABEL.infrastructure },
  { key: 'tooling', label: EXPENSE_CATEGORY_LABEL.tooling },
  { key: 'salary', label: EXPENSE_CATEGORY_LABEL.salary },
  { key: 'marketing', label: EXPENSE_CATEGORY_LABEL.marketing },
  { key: 'other', label: EXPENSE_CATEGORY_LABEL.other },
];

export function IncomeStatementCard({
  statement,
  comparisonStatement,
  comparisonLabel,
}: {
  statement: IncomeStatement;
  comparisonStatement?: IncomeStatement;
  comparisonLabel?: string;
}) {
  const cmp = comparisonStatement;
  return (
    <Card interactive={false}>
      <header className="border-b border-border p-4">
        <h3 className="text-lg font-semibold text-foreground">{statement.period_label}</h3>
        <p className="text-xs text-muted-foreground">
          {statement.period_start} → {statement.period_end}
          {cmp && ` · vs ${comparisonLabel ?? cmp.period_label}`}
        </p>
      </header>

      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-card-subtle text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left">Line</th>
            <th className="px-4 py-2 text-right">{statement.period_label}</th>
            {cmp && <th className="px-4 py-2 text-right">{cmp.period_label}</th>}
            {cmp && <th className="px-4 py-2 text-right">Δ</th>}
          </tr>
        </thead>
        <tbody>
          <tr><td colSpan={cmp ? 4 : 2} className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-success">Revenue</td></tr>
          {REV_ROWS.map((row) => (
            <Row
              key={row.key}
              label={row.label}
              value={statement.revenue[row.key]}
              prev={cmp?.revenue[row.key]}
              polarity="higher-is-better"
            />
          ))}
          <Row label="Total revenue" value={statement.revenue.total} prev={cmp?.revenue.total} polarity="higher-is-better" bold />

          <tr><td colSpan={cmp ? 4 : 2} className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-danger">Expenses</td></tr>
          {EXP_ROWS.map((row) => (
            <Row
              key={row.key}
              label={row.label}
              value={statement.expense[row.key]}
              prev={cmp?.expense[row.key]}
              polarity="lower-is-better"
              hideZero={statement.expense[row.key] === 0 && (!cmp || cmp.expense[row.key] === 0)}
            />
          ))}
          <Row label="Total expenses" value={statement.expense.total} prev={cmp?.expense.total} polarity="lower-is-better" bold />

          <tr className="bg-card-subtle">
            <td className="px-4 py-3 text-sm font-bold text-foreground">Net income</td>
            <td className={`px-4 py-3 text-right font-bold tabular-nums ${statement.net >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatZAR(statement.net)}
            </td>
            {cmp && (
              <>
                <td className={`px-4 py-3 text-right font-bold tabular-nums ${cmp.net >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatZAR(cmp.net)}
                </td>
                <td className={`px-4 py-3 text-right font-medium tabular-nums ${statement.net - cmp.net >= 0 ? 'text-success' : 'text-danger'}`}>
                  {deltaPct(statement.net, cmp.net)}
                </td>
              </>
            )}
          </tr>
        </tbody>
      </table>
      </div>
    </Card>
  );
}

/**
 * Which direction is good news for this line.
 *
 * The delta cell used to be `value - prev >= 0 ? success : danger` for EVERY
 * row, which meant the nine expense categories and Total expenses all painted
 * GREEN when they grew. Salary doubling read as a win; marketing spend halving
 * read as a problem. Only Net income had bespoke handling, so the one correct
 * row sat directly beneath ten inverted ones.
 *
 * There is no safe default here, so there isn't one: every caller states it.
 */
type Polarity = 'higher-is-better' | 'lower-is-better';

function Row({
  label,
  value,
  prev,
  polarity,
  bold,
  hideZero,
}: {
  label: string;
  value: number;
  prev?: number;
  polarity: Polarity;
  bold?: boolean;
  hideZero?: boolean;
}) {
  if (hideZero) return null;
  const cls = bold ? 'font-semibold border-t border-border' : 'text-foreground';
  const rose = prev !== undefined && value - prev >= 0;
  const good = polarity === 'higher-is-better' ? rose : !rose;
  return (
    <tr className={cls}>
      <td className="px-4 py-1.5">{label}</td>
      <td className="px-4 py-1.5 text-right tabular-nums">{formatZAR(value)}</td>
      {prev !== undefined && (
        <>
          <td className="px-4 py-1.5 text-right tabular-nums text-muted-foreground">{formatZAR(prev)}</td>
          <td className={`px-4 py-1.5 text-right text-xs tabular-nums ${good ? 'text-success' : 'text-danger'}`}>
            {deltaPct(value, prev)}
          </td>
        </>
      )}
    </tr>
  );
}

export type { ExpenseBucket };
